import React from 'react';

const pad2 = n => String(n).padStart(2, '0');

// "+HH:MM" / "-HH:MM" offset the browser is actually in at that moment (so it
// tracks DST), instead of the hard-coded -04:00 every write used to carry.
function offsetString(date) {
  const mins = -date.getTimezoneOffset();
  const abs = Math.abs(mins);
  return `${mins >= 0 ? '+' : '-'}${pad2(Math.floor(abs / 60))}:${pad2(abs % 60)}`;
}

export function toReportDateTime(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T`
       + `${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`
       + offsetString(date);
}

// Report dateTimes are shown as the wall-clock time the crew recorded,
// never converted to the viewer's time zone — so the same timestamp reads
// the same on every tab and for every viewer. These read that wall-clock
// part straight off the string.
export function reportWallClock(value) {
  return typeof value === 'string' && value.length >= 16 ? value.slice(0, 19) : '';
}

export function reportTimeOfDay(value, withSeconds = true) {
  const wall = reportWallClock(value);
  return wall ? wall.slice(11, withSeconds ? 19 : 16) : '—';
}

// datetime-local input value ("YYYY-MM-DDTHH:MM[:SS]") -> report dateTime.
// Keeps the offset of the value being edited, so editing a time never moves
// it; a brand-new value takes the browser's offset for that wall-clock time.
export function fromWallClock(local, previous) {
  if (!local) return '';
  const full = local.length === 16 ? `${local}:00` : local.slice(0, 19);
  const prev = typeof previous === 'string' ? /([+-]\d{2}:\d{2}|Z)$/.exec(previous) : null;
  const offset = prev ? (prev[1] === 'Z' ? '+00:00' : prev[1]) : offsetString(new Date(full));
  return full + offset;
}

// Earliest/latest timestamp among a set of attached voice notes ({timestamp}[]
// — see EpcrApp's attachedNotes), or null if none parse. Used to anchor
// generated demo data (Update CAD's eTimes estimate, Pull from Monitor's
// vitals) to the call's actual voice-note timing rather than the current
// wall-clock time.
export function noteTimeRange(notes) {
  const times = (notes || [])
    .map(n => new Date(n.timestamp))
    .filter(d => !isNaN(d.getTime()));
  if (times.length === 0) return null;
  return { earliest: new Date(Math.min(...times)), latest: new Date(Math.max(...times)) };
}

function parseEnumerations(text) {
  const lines = text.split(/\r?\n/);
  const out = {};
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const parts = line.split("|").map(p => p.replace(/^'/, "").replace(/'$/, ""));
    const [, elem, name, code, desc] = parts;
    if (!elem || !code) continue;
    if (!out[elem]) out[elem] = { name, codes: [], byCode: {} };
    out[elem].codes.push({ value: code, label: desc });
    out[elem].byCode[code] = desc;
  }
  return out;
}

function parseList(json, name) {
  const codes = (json?.DefinedList?.Codes?.Code) || [];
  const out = { name, codes: [], byCode: {} };
  for (const c of codes) {
    const value = c.Value?.Value || c.Value;
    const label = c.SuggestedLabel || c.SourceLabel;
    if (!value) continue;
    // countyFips: City.json only — the ANSI county FIPS code for this city,
    // used to auto-fill the paired county field when a city is picked.
    out.codes.push({ value, label, category: c.Category || "", countyFips: c.CountyFips || null });
    out.byCode[value] = label;
  }
  return out;
}

const LIST_FILES = [
  ["CauseOfInjury",            "/epcr-data/lists/CauseOfInjury.json"],
  ["City",                     "/epcr-data/lists/City.json"],
  ["County",                   "/epcr-data/lists/County.json"],
  ["EnvironmentalFoodAllergy", "/epcr-data/lists/EnvironmentalFoodAllergy.json"],
  ["Impression",               "/epcr-data/lists/Impression.json"],
  ["IncidentLocationType",     "/epcr-data/lists/IncidentLocationType.json"],
  ["MedicalSurgicalHistory",   "/epcr-data/lists/MedicalSurgicalHistory.json"],
  ["Medication",               "/epcr-data/lists/Medication.json"],
  ["MedicationAllergy",        "/epcr-data/lists/MedicationAllergy.json"],
  ["PatientActivity",          "/epcr-data/lists/PatientActivity.json"],
  ["Procedure",                "/epcr-data/lists/Procedure.json"],
  ["Protocols",                "/epcr-data/lists/Protocols.json"],
  ["State",                    "/epcr-data/lists/State.json"],
  ["Symptom",                  "/epcr-data/lists/Symptom.json"],
];

// Elements whose options come from the large external JSON lists rather than
// the element spec (the spec contains no enumeration facets for these).
const ELEMENT_LIST_MAP = {
  "eSituation.04": null,
  "eSituation.09": "Symptom",
  "eSituation.10": "Symptom",
  "eSituation.11": "Impression",
  "eSituation.12": "Impression",
  "eMedications.03": "Medication",
  "eProcedures.03":  "Procedure",
  "eHistory.06":  "MedicationAllergy",
  "eHistory.07":  "EnvironmentalFoodAllergy",
  "eHistory.08":  "MedicalSurgicalHistory",
  "eHistory.12":  "Medication",
  "eHistory.16":  "EnvironmentalFoodAllergy",
  "eHistory.17":  "MedicalSurgicalHistory",
  "eScene.09":    "IncidentLocationType",
  "eInjury.01":   "CauseOfInjury",
  "eProtocols.01": "Protocols",
  // GNIS "Civil" feature-class city codes — the spec ships no enumeration
  // (the real GNIS domain is ~200k+ places); City.json is a curated Georgia
  // subset for the demo. eDisposition.04 has no view field yet.
  "ePatient.06":   "City",
  "eScene.17":     "City",
  "eDisposition.04": "City",
  // ANSI 2-digit state FIPS codes — the real domain is small (57 codes
  // total, states + DC + territories), so State.json ships it whole rather
  // than a curated subset. ePatient.08/eDisposition.05 wired for
  // completeness; only eScene.18 has a picker field so far (Incident tab).
  "ePatient.08":   "State",
  "eScene.18":     "State",
  "eDisposition.05": "State",
  // ANSI county FIPS codes — unlike City, Georgia's full 159-county domain
  // is small enough to ship whole (not a curated subset), so any GA county
  // code resolves to plaintext. ePatient.07/eDisposition.06 wired for
  // completeness; only eScene.21 has a picker field so far (Incident tab).
  "ePatient.07":   "County",
  "eScene.21":     "County",
  "eDisposition.06": "County",
};

export const DataContext = React.createContext(null);

// Ensure every repeatable group (maxOccurs="M") that currently holds a single
// plain object is normalised to a one-element array.  NEMSIS exporters often
// omit the array wrapper when only one record exists; this makes the in-memory
// shape uniform so that index-based paths always work.
function normalizeRepeatableGroups(report, groupSpecs) {
  const pcr = report?.EMSDataSet?.Header?.PatientCareReport;
  if (!pcr || !groupSpecs) return report;
  const result = JSON.parse(JSON.stringify(report));
  const pcrCopy = result.EMSDataSet.Header.PatientCareReport;
  for (const spec of Object.values(groupSpecs)) {
    if (spec.maxOccurs !== 'M') continue;
    const parts = spec.path.split('/');
    let obj = pcrCopy;
    for (let i = 0; i < parts.length - 1; i++) {
      if (obj == null) { obj = null; break; }
      obj = obj[parts[i]];
    }
    if (obj == null) continue;
    const key = parts[parts.length - 1];
    const val = obj[key];
    if (val != null && !Array.isArray(val) && typeof val === 'object') {
      obj[key] = [val];
    }
  }
  return result;
}

export function DataProvider({ children }) {
  const [state, setState] = React.useState({
    loaded: false, enums: {}, lists: {}, specs: {}, paths: {}, groups: {}, report: null,
    currentReport: "report_empty.json",
  });

  // Keep a stable ref to group-specs so loadReport can access them without
  // being recreated every time groups changes.
  const groupsRef = React.useRef({});

  // Debug mode — off by default. Toggled by triple-clicking the CADMUS logo
  // in the validation panel (see ValidationPanel in EpcrPage.js). Lives here
  // (not in EpcrApp's own state) so FieldLabel and every other field-level
  // component can read it via the same useData() call they already make,
  // without threading a prop through every view file.
  const [debugMode, setDebugMode] = React.useState(false);
  const toggleDebugMode = React.useCallback(() => setDebugMode(d => !d), []);

  React.useEffect(() => {
    (async () => {
      try {
        const [enumText, reportJson, specsJson, pathsJson, groupsJson, ...listJsons] = await Promise.all([
          fetch("/epcr-data/enumerations.txt").then(r => r.text()),
          fetch("/epcr-data/report_empty.json").then(r => r.json()),
          fetch("/epcr-data/element-specs.json").then(r => r.json()),
          fetch("/epcr-data/section-specs.json").then(r => r.json()),
          fetch("/epcr-data/group-specs.json").then(r => r.json()),
          ...LIST_FILES.map(([, p]) => fetch(p).then(r => r.json())),
        ]);
        const enums = parseEnumerations(enumText);
        const lists = {};
        LIST_FILES.forEach(([name], i) => {
          lists[name] = parseList(listJsons[i], name);
        });
        groupsRef.current = groupsJson;
        const normalizedReport = normalizeRepeatableGroups(reportJson, groupsJson);
        setState(s => ({ ...s, loaded: true, enums, lists, specs: specsJson, paths: pathsJson, groups: groupsJson, report: normalizedReport }));
      } catch (e) {
        console.error("Data load error:", e);
        setState(s => ({ ...s, loaded: true, enums: {}, lists: {}, specs: {}, paths: {}, groups: {}, report: null, error: String(e) }));
      }
    })();
  }, []);

  const loadReport = React.useCallback(async (filename) => {
    try {
      const reportJson = await fetch(`/epcr-data/${filename}`).then(r => r.json());
      const normalizedReport = normalizeRepeatableGroups(reportJson, groupsRef.current);
      setState(s => ({ ...s, report: normalizedReport, currentReport: filename }));
    } catch (e) {
      console.error("Report load error:", e);
    }
  }, []);

  const setReportPath = React.useCallback((path, value) => {
    setState(s => {
      if (!s.report) return s;
      const next = JSON.parse(JSON.stringify(s.report));
      let obj = next.EMSDataSet.Header.PatientCareReport;
      const parts = path.split("/");
      for (let i = 0; i < parts.length - 1; i++) {
        let val = obj[parts[i]];
        if (val == null || typeof val !== "object") {
          obj[parts[i]] = {};
          val = obj[parts[i]];
        }
        if (Array.isArray(val) && !/^\d+$/.test(parts[i + 1])) {
          // Auto-dereference to first item when no explicit numeric index follows.
          if (val.length === 0) val.push({});
          obj = val[0];
        } else {
          obj = val;
        }
      }
      obj[parts[parts.length - 1]] = value;
      return { ...s, report: next };
    });
  }, []);

  const value = React.useMemo(() => ({
    ...state,
    loadReport,
    setReportPath,
    debugMode,
    toggleDebugMode,

    // Return the compiled spec for a NEMSIS element number, or null.
    spec: (elem) => state.specs[elem] || null,

    // Return the canonical JSON path for a NEMSIS element number, or null.
    // Derived from section specification XMLs; e.g. "eDisposition.27" →
    // "eDisposition/eDisposition.IncidentDispositionGroup/eDisposition.27".
    path: (elem) => state.paths[elem] || null,

    // Return group spec by group number, or null.
    // e.g. "eMedications.MedicationGroup" → { section, path, maxOccurs, parentGroup? }
    groupSpec: (groupNumber) => state.groups[groupNumber] || null,

    // Resolve a stored code (or array of codes) to a human-readable label.
    // Checks spec enumerations → spec NV/PN → external lists → enumerations.txt.
    label: (elem, code) => {
      if (code == null || code === "") return "";
      if (Array.isArray(code)) return code.map(c => resolveLabel(elem, c, state)).filter(Boolean);
      return resolveLabel(elem, code, state);
    },

    // Return the ordered option list for a picker.
    // Priority: spec enumerations → external JSON list → enumerations.txt.
    options: (elem) => {
      const spec = state.specs[elem];
      if (spec?.enumerations?.length > 0) return spec.enumerations;
      const listName = ELEMENT_LIST_MAP[elem];
      if (listName && state.lists[listName]) return state.lists[listName].codes;
      return state.enums[elem]?.codes || [];
    },
  }), [state, setReportPath, debugMode, toggleDebugMode]);

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

function resolveLabel(elem, code, state) {
  if (code == null || code === "") return "";
  const spec = state.specs[elem];
  // Check spec enumerations
  if (spec?.enumerations?.length) {
    const match = spec.enumerations.find(e => e.value === String(code));
    if (match) return match.label;
  }
  // Check NV codes (7701xxx) — shared across elements but listed per-element in spec
  if (spec?.nv?.length) {
    const match = spec.nv.find(e => e.code === String(code));
    if (match) return match.label;
  }
  // Check PN codes (8801xxx)
  if (spec?.pn?.length) {
    const match = spec.pn.find(e => e.code === String(code));
    if (match) return match.label;
  }
  // Fall back to external list
  const listName = ELEMENT_LIST_MAP[elem];
  if (listName && state.lists[listName]?.byCode[code]) return state.lists[listName].byCode[code];
  // Fall back to enumerations.txt
  if (state.enums[elem]?.byCode[code]) return state.enums[elem].byCode[code];
  return String(code);
}

export function useData() { return React.useContext(DataContext); }

export function useField(path) {
  const data = useData();
  if (!data?.report) return null;
  let obj = data.report.EMSDataSet.Header.PatientCareReport;
  const parts = path.split("/");
  for (const p of parts) {
    if (obj == null) return null;
    obj = obj[p];
  }
  return obj;
}
